import { useEffect, useMemo, useRef, useState } from "react";
import { askAssistant, fetchDoctor, fetchProjectExports, type CloudProjectSummary, type DoctorReport, type ProjectExportItem } from "../api/client";
import type {
  Character,
  EasyModeOptions,
  EasyModeProgress,
  FilmProject,
  Scene,
  SceneTake,
  StoryboardFrame
} from "../types";
import type { AssetLook, MatchedPerson, MatchedPlace } from "../utils/assetMatching";
import { assetDisplayUrl, frameDisplayUrl, matchPeople, matchPlaces, pickLookForScene } from "../utils/assetMatching";
import { agentPlanSummary } from "../utils/analysisSummary";
import { easyModeProgressPercent } from "../utils/easyMode";
import { projectLogline, sceneDialogue, sceneDirectorPage, voiceTracksForScene } from "../utils/scenePage";
import {
  animaticEntrySeconds,
  animaticSupported,
  animaticTotalSeconds,
  downloadAnimatic,
  exportAnimatic,
  type AnimaticProgress,
  type AnimaticScript
} from "../utils/animatic";
import { clampRuntimeSeconds } from "../utils/storyboard";

/**
 * Simple mode: the Apple-grade front-of-house. One spine — drop a script, meet your film,
 * one button, watch it assemble, review the cut. Everything here calls the same handlers
 * and data the Pro workspace uses; no engine logic lives in this file.
 */

export interface SimpleSceneRemakeRequest {
  sceneId: string;
  panelsPerScene?: number;
  /** When set, only these boards regenerate — "re-make just this shot". */
  frameIds?: string[];
  /** Chosen look per character name — passed as priority conditioning references. */
  lookOverrides: Record<string, AssetLook>;
  /** Chosen environment plate for this scene — passed as a priority sceneReference. */
  placeLook?: AssetLook;
  /** Optional slugline time-of-day override, e.g. "NIGHT" | "DUSK" | "DAY". */
  mood?: string;
}

/** Minimal shape the look picker needs — satisfied by both MatchedPerson and MatchedPlace. */
interface LookOwner {
  name: string;
  looks: AssetLook[];
  defaultLook?: AssetLook;
}

/** Escape closes the sheet — shared by every Simple-mode dialog. */
function useEscape(onClose: () => void) {
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
}

/** Quiet system feedback for Simple mode: the latest notice as an auto-hiding toast. */
function SimpleToast({ notice }: { notice: string }) {
  const [visible, setVisible] = useState(false);
  const firstNotice = useRef(notice);
  useEffect(() => {
    if (notice === firstNotice.current || !notice) return;
    setVisible(true);
    const timer = window.setTimeout(() => setVisible(false), 5200);
    return () => window.clearTimeout(timer);
  }, [notice]);
  if (!visible) return null;
  return (
    <button className="simple-toast" onClick={() => setVisible(false)} type="button" aria-live="polite">
      {notice}
    </button>
  );
}

interface SimpleModeProps {
  project: FilmProject;
  isAnalyzing: boolean;
  isEasyModeRunning: boolean;
  easyModeProgress: EasyModeProgress | null;
  easyModeOptions: EasyModeOptions;
  onDropScript: (file: File) => void;
  onAnalyze: () => void;
  onJustMakeIt: () => void;
  onTrySample: () => void;
  onNewFromImages: (files: File[]) => void;
  isJustMaking: boolean;
  apiStatus: "checking" | "online" | "offline";
  engineStale: boolean;
  onMakeFilm: (options: EasyModeOptions, extras?: { stylePreset?: string; runtimeSeconds?: number }) => void;
  onRemakeScene: (request: SimpleSceneRemakeRequest) => void;
  onRestoreTake: (sceneId: string, takeId: string) => void;
  onSaveSceneNote: (sceneId: string, text: string) => void;
  onShareLink: () => void;
  isSharingLink: boolean;
  onMakePoster: () => void;
  isMakingPoster: boolean;
  onUploadPersonPhoto: (personName: string, files: File[]) => void;
  onUploadPlacePhotos: (files: File[]) => void;
  /** Home-screen uploads, before the script is even read — yours always wins over generated. */
  onUploadAssets: (bucket: "characterCards" | "sceneReferences" | "voiceAudio", files: File[]) => void;
  onOpenPro: () => void;
  onSendToRender: () => Promise<boolean>;
  isSavingPackage: boolean;
  notice: string;
  films: CloudProjectSummary[];
  isLoadingFilms: boolean;
  onRefreshFilms: () => void;
  onOpenFilm: (projectId: string) => void;
  onNewFilm: () => void;
}

/** "Your films": yesterday's drafts and a fresh start, without leaving Simple. */
function FilmsSheet({
  currentProject,
  films,
  isLoading,
  onOpen,
  onNew,
  onClose
}: {
  currentProject: FilmProject;
  films: CloudProjectSummary[];
  isLoading: boolean;
  onOpen: (projectId: string) => void;
  onNew: () => void;
  onClose: () => void;
}) {
  useEscape(onClose);
  const others = films.filter((film) => film.id !== currentProject.id);
  return (
    <div className="simple-sheet-backdrop" role="presentation" onClick={onClose}>
      <section className="simple-sheet" role="dialog" aria-modal="true" aria-label="Your films" onClick={(event) => event.stopPropagation()}>
        <h2>Your films</h2>
        <div className="simple-films">
          <article className="simple-film-row current">
            <div>
              <strong>{currentProject.title}</strong>
              <small>{currentProject.scenes.length} scene{currentProject.scenes.length === 1 ? "" : "s"} · open now</small>
            </div>
            <b>Current</b>
          </article>
          {others.map((film) => (
            <article className="simple-film-row" key={film.id}>
              <div>
                <strong>{film.title || "Untitled film"}</strong>
                <small>
                  {film.sceneCount || 0} scene{(film.sceneCount || 0) === 1 ? "" : "s"}
                  {film.updatedAt ? ` · ${new Date(film.updatedAt).toLocaleDateString()}` : ""}
                </small>
              </div>
              <button
                className="simple-pill"
                onClick={() => {
                  onOpen(film.id);
                  onClose();
                }}
                type="button"
              >
                Open
              </button>
            </article>
          ))}
          {isLoading ? <p className="simple-footnote">Looking for saved films…</p> : null}
          {!isLoading && !others.length ? <p className="simple-footnote">Saved films appear here once cloud autosave has them.</p> : null}
        </div>
        <button
          className="simple-pill primary big"
          onClick={() => {
            onNew();
            onClose();
          }}
          type="button"
        >
          + New film
        </button>
        <p className="simple-footnote">Your current film keeps autosaving — switching never loses work.</p>
      </section>
    </div>
  );
}

type SimpleScreen = "home" | "reading" | "film" | "assembling" | "cut";

function hasParsedScript(project: FilmProject): boolean {
  return project.scenes.length > 1 || (project.scenes.length === 1 && project.characters.length > 0);
}

function generatedFrames(project: FilmProject): StoryboardFrame[] {
  // Only real boards count toward a "first cut": ChatGPT-generated images or the user's uploads.
  // Internal SVG sketches (source "internal", status "saved") are placeholders, not a cut.
  return project.scenes
    .flatMap((scene) => scene.frames)
    .filter((frame) =>
      frame.source === "generated" ||
      frame.source === "uploaded" ||
      frame.generationStatus === "generated" ||
      frame.generationStatus === "uploaded"
    );
}

function deriveScreen(project: FilmProject, isAnalyzing: boolean, running: boolean, progress: EasyModeProgress | null): SimpleScreen {
  if (isAnalyzing) return "reading";
  if (running || (progress && !progress.finishedAt)) return "assembling";
  if (!project.scriptUpload && !hasParsedScript(project)) return "home";
  if (!hasParsedScript(project)) return "home";
  if (generatedFrames(project).length > 0) return "cut";
  return "film";
}

function lookSrc(look?: AssetLook, width?: number): string {
  return look ? assetDisplayUrl(look.asset, width) : "";
}

function initials(name: string): string {
  return name.split(/\s+/).map((part) => part[0] || "").join("").slice(0, 2).toUpperCase();
}

/* ---------- Screen 1: Drop your script ---------- */

function SimpleHome({
  project,
  onDropScript,
  onAnalyze,
  onJustMakeIt,
  onTrySample,
  onNewFromImages,
  onAddAssets
}: {
  project: FilmProject;
  onDropScript: (file: File) => void;
  onAnalyze: () => void;
  onJustMakeIt: () => void;
  onTrySample: () => void;
  onNewFromImages: (files: File[]) => void;
  onAddAssets: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const staged = project.scriptUpload;
  const manifest = project.uploadedAssets;
  const yourAssetCount =
    (manifest?.characterCards || []).filter((a) => a.sourceMode === "user_upload" && !a.disabled).length +
    (manifest?.sceneReferences || []).filter((a) => a.sourceMode === "user_upload" && !a.disabled).length +
    (manifest?.voiceAudio || []).filter((a) => a.sourceMode === "user_upload" && !a.disabled).length;
  return (
    <main className="simple-screen simple-home">
      <h1>From script to screen.</h1>
      <p className="simple-sub">
        Drop your screenplay. Kinetix finds your people and places, then builds your film.
      </p>
      <input
        ref={inputRef}
        className="visually-hidden"
        type="file"
        accept=".pdf,.fdx,.txt,.fountain,text/plain,application/pdf"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) onDropScript(file);
          event.target.value = "";
        }}
      />
      <button
        className="simple-dropzone"
        type="button"
        onClick={() => inputRef.current?.click()}
        onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => {
          event.preventDefault();
          const file = event.dataTransfer.files?.[0];
          if (file) onDropScript(file);
        }}
      >
        <span className="simple-drop-glyph" aria-hidden="true">↑</span>
        {staged ? (
          <>
            <strong>{staged.fileName}</strong>
            <small>{Math.max(1, Math.round(staged.size / 1024))} KB ready</small>
          </>
        ) : (
          <>
            <strong>Drop your script here</strong>
            <small>PDF, Final Draft, or plain text</small>
          </>
        )}
        <span className="simple-pill primary">{staged ? "Choose a different file" : "Choose a file"}</span>
      </button>
      <button className="simple-add-assets" onClick={onAddAssets} type="button">
        {yourAssetCount
          ? `🎞 ${yourAssetCount} of your assets ready — add more`
          : "🎞 Have photos or voice reads? Add your own cast & places"}
      </button>
      {yourAssetCount ? (
        <p className="simple-footnote">They'll be matched to your script automatically — ChatGPT only makes what's missing.</p>
      ) : null}
      {staged ? (
        <div className="simple-cta-bar">
          <button className="simple-pill primary big" onClick={onJustMakeIt} type="button">
            ✨ Just make it
          </button>
          <button className="simple-link" onClick={onAnalyze} type="button">
            Read my script first and let me review
          </button>
        </div>
      ) : (
        <div className="simple-cta-bar">
          <button className="simple-link simple-sample-link" onClick={onTrySample} type="button">
            No script handy? ✨ Try a sample script
          </button>
          <label className="simple-link simple-sample-link">
            🖼️ Or start a film from images
            <input
              accept="image/*,.png,.jpg,.jpeg,.webp"
              className="visually-hidden"
              multiple
              onChange={(event) => {
                const files = Array.from(event.target.files || []);
                if (files.length) onNewFromImages(files);
                event.target.value = "";
              }}
              type="file"
            />
          </label>
        </div>
      )}
      <p className="simple-footnote">Your script stays yours. Nothing renders until you say so.</p>
    </main>
  );
}

/* ---------- Screen 2: Reading ---------- */

function SimpleReading({ project }: { project: FilmProject }) {
  const title = project.title || project.scriptUpload?.fileName || "your script";
  return (
    <main className="simple-screen simple-reading">
      <h1>Reading {title}…</h1>
      <div className="simple-progress-track indeterminate" role="progressbar" aria-label="Reading script">
        <span />
      </div>
      <p className="simple-sub">Finding your people, your places, and the strongest beats.</p>
      <p className="simple-footnote">Takes about a minute. We'll lay everything out for you.</p>
    </main>
  );
}

/* ---------- Screen 3: Your film ---------- */

function PersonCard({ person, character, onAddPhoto }: { person: MatchedPerson; character?: Character; onAddPhoto: (name: string) => void }) {
  const src = lookSrc(person.defaultLook, 448);
  const hasUpload = Boolean(person.defaultLook);
  const generated = character?.cardStatus === "generated";
  return (
    <article className={`simple-person${hasUpload ? " mine" : ""}`}>
      <div className="simple-person-photo">
        {src ? <img loading="lazy" src={src} alt={`${person.name} reference`} /> : <span className="simple-person-initials">{initials(person.name)}</span>}
        {hasUpload ? <b className="simple-photo-badge">✓ Your photo</b> : null}
      </div>
      <div className="simple-person-meta">
        <strong>{person.name}</strong>
        <small className={hasUpload ? "mine" : ""}>
          {hasUpload
            ? person.looks.length > 1
              ? `From your uploads · ${person.looks.length} looks`
              : "From your upload"
            : generated
              ? "Look made for you"
              : "A look will be made for them"}
        </small>
        <button className="simple-person-add" onClick={() => onAddPhoto(person.name)} type="button">
          {hasUpload ? "Add another look" : "Use my photo"}
        </button>
      </div>
    </article>
  );
}

function SimpleFilm({
  project,
  people,
  places,
  onMakeFilm,
  onJustMakeIt,
  onUploadPersonPhoto,
  onUploadPlacePhotos,
  easyModeOptions
}: {
  project: FilmProject;
  people: MatchedPerson[];
  places: MatchedPlace[];
  onMakeFilm: (options: EasyModeOptions, extras?: { stylePreset?: string; runtimeSeconds?: number }) => void;
  onJustMakeIt: () => void;
  onUploadPersonPhoto: (personName: string, files: File[]) => void;
  onUploadPlacePhotos: (files: File[]) => void;
  easyModeOptions: EasyModeOptions;
}) {
  const [showSheet, setShowSheet] = useState(false);
  const [photoFor, setPhotoFor] = useState<string | null>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const placeInputRef = useRef<HTMLInputElement>(null);
  const uploadedCount = people.filter((person) => person.defaultLook).length;
  const scriptPeople = people.filter((person) => person.inScript);
  const summary = uploadedCount
    ? `We found ${scriptPeople.length} people and ${project.scenes.length} scenes. ${uploadedCount === 1 ? "One of them already has" : `${uploadedCount} of them already have`} your photos — everyone else gets a look you can swap anytime.`
    : `We found ${scriptPeople.length} people and ${project.scenes.length} scenes. Everyone gets a look you can swap anytime.`;
  return (
    <main className="simple-screen simple-film">
      <span className="simple-eyebrow">{project.title} · {project.scenes.length} scenes</span>
      <h1>Here's your film.</h1>
      <p className="simple-sub">{summary}</p>
      <input
        ref={photoInputRef}
        className="visually-hidden"
        type="file"
        accept="image/*"
        multiple
        onChange={(event) => {
          const files = Array.from(event.target.files || []);
          if (files.length && photoFor) onUploadPersonPhoto(photoFor, files);
          event.target.value = "";
          setPhotoFor(null);
        }}
      />
      <div className="simple-people" aria-label="People in your film">
        {people.map((person) => (
          <PersonCard
            key={person.characterId || person.name}
            person={person}
            character={project.characters.find((c) => c.id === person.characterId)}
            onAddPhoto={(name) => {
              setPhotoFor(name);
              photoInputRef.current?.click();
            }}
          />
        ))}
      </div>
      <input
        ref={placeInputRef}
        className="visually-hidden"
        type="file"
        accept="image/*"
        multiple
        onChange={(event) => {
          const files = Array.from(event.target.files || []);
          if (files.length) onUploadPlacePhotos(files);
          event.target.value = "";
        }}
      />
      <div className="simple-places" aria-label="Places in your film">
        {places.map((place) => {
          const src = lookSrc(place.defaultLook, 640);
          return (
            <article className="simple-place" key={place.name}>
              {src ? <img loading="lazy" src={src} alt={`${place.name} reference`} /> : <span />}
              <strong>{place.name}</strong>
              {place.looks.length > 1 ? <b>{place.looks.length} looks</b> : null}
            </article>
          );
        })}
        <button className="simple-place add" onClick={() => placeInputRef.current?.click()} type="button">
          <strong>+ Add place photos</strong>
          <small>Name files after the location</small>
        </button>
      </div>
      <div className="simple-cta-bar">
        <button className="simple-pill primary big" onClick={onJustMakeIt} type="button">
          ✨ Just make it
        </button>
        <button className="simple-link" onClick={() => setShowSheet(true)} type="button">
          Choose length, look &amp; finish first
        </button>
        <small>About 4 minutes · draft quality · you approve everything before it renders</small>
      </div>
      {showSheet ? (
        <MakeFilmSheet
          defaults={easyModeOptions}
          currentStyle={project.stylePreset}
          currentRuntime={project.runtimeSeconds}
          onClose={() => setShowSheet(false)}
          onStart={(options, extras) => {
            setShowSheet(false);
            onMakeFilm(options, extras);
          }}
        />
      ) : null}
    </main>
  );
}

/* ---------- Screen 4: Make my film sheet ---------- */

const FILM_LOOKS: Array<{ id: string; label: string; hint: string }> = [
  { id: "Cinematic Realism", label: "Cinematic", hint: "rich & real" },
  { id: "Graphic Noir", label: "Noir", hint: "stark & moody" },
  { id: "Previs Sketch", label: "Sketch", hint: "fast previz" },
  { id: "Commercial Clean", label: "Clean", hint: "bright & crisp" }
];

const FILM_LENGTHS: Array<{ seconds: number; label: string; hint: string }> = [
  { seconds: 15, label: "Short", hint: "0:15" },
  { seconds: 30, label: "Teaser", hint: "0:30" },
  { seconds: 60, label: "Trailer", hint: "1:00" },
  { seconds: 300, label: "Short film", hint: "5:00" }
];

function MakeFilmSheet({
  defaults,
  currentStyle,
  currentRuntime,
  onClose,
  onStart
}: {
  defaults: EasyModeOptions;
  currentStyle: string;
  currentRuntime: number;
  onClose: () => void;
  onStart: (options: EasyModeOptions, extras: { stylePreset: string; runtimeSeconds: number }) => void;
}) {
  useEscape(onClose);
  const [quality, setQuality] = useState<EasyModeOptions["imageQuality"]>(defaults.imageQuality);
  const [style, setStyle] = useState(FILM_LOOKS.some((look) => look.id === currentStyle) ? currentStyle : FILM_LOOKS[0].id);
  const [runtime, setRuntime] = useState(clampRuntimeSeconds(currentRuntime, 60));
  return (
    <div className="simple-sheet-backdrop" role="presentation" onClick={onClose}>
      <section className="simple-sheet" role="dialog" aria-modal="true" aria-label="Make my film" onClick={(event) => event.stopPropagation()}>
        <h2>Make my film</h2>
        <div className="simple-choice">
          <span>Length</span>
          <div className="simple-choice-options">
            {FILM_LENGTHS.map((item) => (
              <button className={runtime === item.seconds ? "active" : ""} key={item.seconds} onClick={() => setRuntime(item.seconds)} type="button">
                <strong>{item.label}</strong>
                <small>{item.hint}</small>
              </button>
            ))}
          </div>
          <label className="simple-custom-runtime">
            <span>Seconds</span>
            <input
              aria-label="Custom film length seconds"
              min={1}
              onChange={(event) => setRuntime(clampRuntimeSeconds(Number(event.target.value), runtime))}
              step={1}
              type="number"
              value={runtime}
            />
          </label>
        </div>
        <div className="simple-choice">
          <span>Look</span>
          <div className="simple-choice-options">
            {FILM_LOOKS.map((look) => (
              <button className={style === look.id ? "active" : ""} key={look.id} onClick={() => setStyle(look.id)} type="button">
                <strong>{look.label}</strong>
                <small>{look.hint}</small>
              </button>
            ))}
          </div>
        </div>
        <div className="simple-choice">
          <span>Finish</span>
          <div className="simple-choice-options">
            <button className={quality === "low" ? "active" : ""} onClick={() => setQuality("low")} type="button">
              <strong>Draft</strong>
              <small>fast · perfect first pass</small>
            </button>
            <button className={quality === "medium" ? "active" : ""} onClick={() => setQuality("medium")} type="button">
              <strong>Standard</strong>
              <small>balanced</small>
            </button>
            <button className={quality === "high" ? "active" : ""} onClick={() => setQuality("high")} type="button">
              <strong>Final</strong>
              <small>sharper · slower</small>
            </button>
          </div>
        </div>
        <button
          className="simple-pill primary big"
          onClick={() => onStart({ ...defaults, sceneScope: "all", imageQuality: quality, stylePreset: style }, { stylePreset: style, runtimeSeconds: runtime })}
          type="button"
        >
          Start
        </button>
        <p className="simple-footnote">You can re-make any single scene later — nothing is final.</p>
      </section>
    </div>
  );
}

/* ---------- Screen 5: Assembling ---------- */

function SimpleAssembling({ project, progress }: { project: FilmProject; progress: EasyModeProgress | null }) {
  const percent = progress ? easyModeProgressPercent(progress) : 8;
  const runningStep = progress?.steps.find((step) => step.status === "running");
  const uploadNote = (project.uploadedAssets?.characterCards || []).length
    ? "Your photos are used in every shot their people appear in."
    : "Every look carries through shot to shot.";
  // The casting call: as cards and plates land in the manifest, faces appear in real time.
  const people = useMemo(() => matchPeople(project.characters, project.uploadedAssets), [project.characters, project.uploadedAssets]);
  const places = useMemo(() => matchPlaces(project.scenes, project.uploadedAssets), [project.scenes, project.uploadedAssets]);
  const castInScript = people.filter((person) => person.inScript);
  return (
    <main className="simple-screen simple-assembling">
      <h1>Making {project.title}</h1>
      <p className="simple-sub">{runningStep ? runningStep.label : "Getting started…"}{runningStep?.detail ? ` · ${runningStep.detail}` : ""}</p>
      <div className="simple-progress-track" role="progressbar" aria-valuenow={percent} aria-valuemin={0} aria-valuemax={100}>
        <span style={{ width: `${percent}%` }} />
      </div>
      {castInScript.length ? (
        <div className="simple-casting" aria-label="Your cast, appearing as they're made">
          {castInScript.map((person) => {
            const src = lookSrc(person.defaultLook, 128);
            return (
              <figure className={src ? "cast" : "waiting"} key={person.name}>
                <span className="simple-casting-face">
                  <i>{initials(person.name)}</i>
                  {src ? <img src={src} alt={person.name} onError={(event) => { event.currentTarget.style.display = "none"; }} /> : null}
                </span>
                <figcaption>{person.name}</figcaption>
              </figure>
            );
          })}
          {places.slice(0, 6).map((place) => {
            const src = lookSrc(place.defaultLook, 192);
            return src ? (
              <figure className="cast place" key={`place-${place.name}`}>
                <span className="simple-casting-face place">
                  <img src={src} alt={place.name} />
                </span>
                <figcaption>{place.name}</figcaption>
              </figure>
            ) : null;
          })}
        </div>
      ) : null}
      <div className="simple-strip" aria-hidden="true">
        {project.scenes.slice(0, 12).map((scene, index) => {
          const done = scene.frames.some((frame) => frame.generationStatus === "generated" || frame.source === "generated");
          const active = !done && index === project.scenes.findIndex((s) => !s.frames.some((f) => f.generationStatus === "generated" || f.source === "generated"));
          return <span className={done ? "done" : active ? "active" : ""} key={scene.id} />;
        })}
      </div>
      <p className="simple-footnote">{uploadNote} Nothing is sent to the video provider.</p>
    </main>
  );
}

/* ---------- Screens 6–8: First cut, scene sheet, look picker ---------- */

function sceneCover(scene: Scene): string {
  const frame = scene.frames.find((f) => f.imageUrl || f.sourceUrl || f.storageKey);
  return frame ? frameDisplayUrl(frame) : "";
}

const CUT_HINTS_KEY = "kinetix_film_cut_hints_v1";

/** 44-byte silent WAV: played once inside the user's click to unlock the audio element for later VO. */
const SILENT_WAV = "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=";

interface PlayheadEntry {
  scene: Scene;
  frame: StoryboardFrame;
  src: string;
}

export function SimpleCut({
  project,
  people,
  places,
  onRemakeScene,
  onRestoreTake,
  onSaveSceneNote,
  onShareLink,
  isSharingLink,
  onMakePoster,
  isMakingPoster,
  onSendToRender,
  isSavingPackage,
  isEasyModeRunning,
  readOnly = false
}: {
  project: FilmProject;
  people: MatchedPerson[];
  places: MatchedPlace[];
  onRemakeScene: (request: SimpleSceneRemakeRequest) => void;
  onRestoreTake: (sceneId: string, takeId: string) => void;
  onSaveSceneNote: (sceneId: string, text: string) => void;
  onShareLink: () => void;
  isSharingLink: boolean;
  onMakePoster: () => void;
  isMakingPoster: boolean;
  onSendToRender: () => Promise<boolean>;
  isSavingPackage: boolean;
  isEasyModeRunning: boolean;
  /** Share-link viewer: watching and downloading allowed, changing and rendering hidden. */
  readOnly?: boolean;
}) {
  const [selectedSceneId, setSelectedSceneId] = useState(project.scenes[0]?.id || "");
  const [sceneOpen, setSceneOpen] = useState(false);
  const [showPitch, setShowPitch] = useState(false);
  const [showRender, setShowRender] = useState(false);
  // "Meet your film": tap a face or place on the strip to browse every look.
  const [castFor, setCastFor] = useState<{ owner: LookOwner; isPlace: boolean } | null>(null);
  // playIndex: null = stopped, -1 = title card, 0..n = boards, -2 = end card.
  const [playIndex, setPlayIndex] = useState<number | null>(null);
  const playerRef = useRef<HTMLDivElement>(null);
  const scene = project.scenes.find((item) => item.id === selectedSceneId) || project.scenes[0];
  const cover = scene ? sceneCover(scene) : "";

  // Every displayable board in script order — the animatic timeline.
  const playlist = useMemo<PlayheadEntry[]>(
    () =>
      project.scenes.flatMap((item) =>
        item.frames
          .filter((frame) => !frame.disabled)
          .map((frame) => ({ scene: item, frame, src: frameDisplayUrl(frame) }))
          .filter((entry) => entry.src)
      ),
    [project.scenes]
  );
  const playing = playIndex !== null && playlist.length > 0;
  const onTitleCard = playIndex === -1;
  const onEndCard = playIndex === -2;
  const playEntry = playing && playIndex !== null && playIndex >= 0 ? playlist[Math.min(playIndex, playlist.length - 1)] : null;

  // Watch mode: title card → each board holds for its timing (clamped to animatic pacing) → end card.
  useEffect(() => {
    if (playIndex === null) return;
    const seconds = onTitleCard ? 2.4 : onEndCard ? 2.6 : Math.min(6, Math.max(1.6, playEntry?.frame.durationSeconds || 3));
    const timer = window.setTimeout(() => {
      setPlayIndex((current) => {
        if (current === null) return null;
        if (current === -1) return 0;
        if (current === -2) return null; // lights up
        const next = current + 1;
        if (next >= playlist.length) return -2; // fade to end card
        return next;
      });
    }, seconds * 1000);
    return () => window.clearTimeout(timer);
  }, [playIndex, onTitleCard, onEndCard, playEntry, playlist.length]);

  // Keep the scene strip in sync while watching.
  useEffect(() => {
    if (playEntry) setSelectedSceneId(playEntry.scene.id);
  }, [playEntry]);

  // Voice-over: when the playhead enters a scene, play its matched VO reads in line order.
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioSceneRef = useRef<string>("");
  useEffect(() => {
    const sceneId = playEntry?.scene.id || "";
    if (!sceneId || playIndex === null) {
      audioRef.current?.pause();
      audioSceneRef.current = "";
      return;
    }
    if (audioSceneRef.current === sceneId) return; // same scene — let the current read finish
    audioSceneRef.current = sceneId;
    const tracks = voiceTracksForScene(project, sceneId, project.uploadedAssets);
    audioRef.current?.pause();
    if (!tracks.length) return;
    const audio = audioRef.current || new Audio();
    audioRef.current = audio;
    let trackIndex = 0;
    const playTrack = () => {
      const track = tracks[trackIndex];
      if (!track) return;
      audio.src = assetDisplayUrl(track.asset);
      audio.onended = () => {
        trackIndex += 1;
        if (trackIndex < tracks.length && audioSceneRef.current === sceneId) playTrack();
      };
      void audio.play().catch(() => undefined);
    };
    playTrack();
  }, [playEntry?.scene.id, playIndex, project]);

  // Stop any VO when playback stops or the screen unmounts.
  useEffect(() => () => audioRef.current?.pause(), []);

  // Arrow keys scrub scenes when no sheet is open.
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (sceneOpen || showPitch) return;
      if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return;
      setPlayIndex(null);
      setSelectedSceneId((currentId) => {
        const index = project.scenes.findIndex((item) => item.id === currentId);
        const next = event.key === "ArrowRight" ? index + 1 : index - 1;
        return project.scenes[Math.min(project.scenes.length - 1, Math.max(0, next))]?.id || currentId;
      });
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [project.scenes, sceneOpen, showPitch]);

  const displaySrc = playEntry ? playEntry.src : cover;
  const displayScene = playEntry ? playEntry.scene : scene;

  // Animatic export: the same cut, captions and VO the player shows, filmed to a video file.
  const [exportProgress, setExportProgress] = useState<AnimaticProgress | null>(null);
  const [exportNote, setExportNote] = useState("");
  const exportAbortRef = useRef<AbortController | null>(null);
  useEffect(() => {
    if (!exportNote) return;
    const timer = window.setTimeout(() => setExportNote(""), 6000);
    return () => window.clearTimeout(timer);
  }, [exportNote]);

  const exportScript = useMemo<AnimaticScript>(() => {
    const linesCache = new Map<string, ReturnType<typeof sceneDialogue>>();
    const sceneCounts = new Map<string, number>();
    playlist.forEach((entry) => sceneCounts.set(entry.scene.id, (sceneCounts.get(entry.scene.id) || 0) + 1));
    const scenePositions = new Map<string, number>();
    const entries = playlist.map((entry) => {
      let lines = linesCache.get(entry.scene.id);
      if (!lines) {
        lines = sceneDialogue(project, entry.scene.id);
        linesCache.set(entry.scene.id, lines);
      }
      const position = scenePositions.get(entry.scene.id) || 0;
      scenePositions.set(entry.scene.id, position + 1);
      const count = sceneCounts.get(entry.scene.id) || 1;
      const caption = lines.length ? lines[Math.min(lines.length - 1, Math.floor((position * lines.length) / count))] : null;
      return {
        sceneId: entry.scene.id,
        src: entry.src,
        durationSeconds: animaticEntrySeconds(entry.frame.durationSeconds),
        caption
      };
    });
    const voiceUrlsBySceneId: Record<string, string[]> = {};
    for (const item of project.scenes) {
      const tracks = voiceTracksForScene(project, item.id, project.uploadedAssets);
      if (tracks.length) voiceUrlsBySceneId[item.id] = tracks.map((track) => assetDisplayUrl(track.asset));
    }
    return { title: project.title, aspectRatio: project.aspectRatio, entries, voiceUrlsBySceneId };
  }, [playlist, project]);

  async function handleSaveVideo() {
    if (exportProgress) return;
    setPlayIndex(null);
    audioRef.current?.pause();
    audioSceneRef.current = "";
    const controller = new AbortController();
    exportAbortRef.current = controller;
    setExportProgress({ elapsedSeconds: 0, totalSeconds: animaticTotalSeconds(exportScript) });
    try {
      const result = await exportAnimatic(exportScript, { onProgress: setExportProgress, signal: controller.signal });
      downloadAnimatic(result, project.title);
      setExportNote(`Saved to your downloads as a .${result.extension} — ready to send.`);
    } catch (error) {
      if ((error as DOMException)?.name === "AbortError") setExportNote("");
      else setExportNote(error instanceof Error ? error.message : "The recording didn't finish — try again.");
    } finally {
      setExportProgress(null);
      exportAbortRef.current = null;
    }
  }

  // Read-along captions: pace the scene's dialogue evenly across its boards so the
  // words advance as the cut plays — like subtitles on a temp screening.
  const watchCaption = useMemo(() => {
    if (!playEntry) return null;
    const lines = sceneDialogue(project, playEntry.scene.id);
    if (!lines.length) return null;
    const sceneEntries = playlist.filter((entry) => entry.scene.id === playEntry.scene.id);
    const pos = Math.max(0, sceneEntries.findIndex((entry) => entry.frame.id === playEntry.frame.id));
    const index = Math.min(lines.length - 1, Math.floor((pos * lines.length) / Math.max(1, sceneEntries.length)));
    return lines[index];
  }, [playEntry, playlist, project]);

  // Browsers only allow audio that begins inside a user gesture; priming the element here lets
  // the VO start later, when the title card hands off to scene 1.
  function unlockAudio() {
    const audio = audioRef.current || new Audio();
    audioRef.current = audio;
    audio.muted = true;
    audio.src = SILENT_WAV;
    void audio.play().then(() => audio.pause()).catch(() => undefined);
    audio.muted = false;
  }

  function startWatch() {
    if (showHints) dismissHints();
    unlockAudio();
    setPlayIndex(-1);
  }

  function startPresent() {
    unlockAudio();
    setPlayIndex(-1);
    playerRef.current?.requestFullscreen?.().catch(() => undefined);
  }

  // One-time pointers for first-ever cut visitors; any interaction dismisses them for good.
  const [showHints, setShowHints] = useState(() => {
    try {
      return !readOnly && !window.localStorage.getItem(CUT_HINTS_KEY);
    } catch {
      return false;
    }
  });
  function dismissHints() {
    setShowHints(false);
    try {
      window.localStorage.setItem(CUT_HINTS_KEY, "seen");
    } catch {
      // private mode — hints just reappear next visit
    }
  }

  return (
    <main className="simple-screen simple-cut">
      <h1>{project.title} — first cut</h1>
      <p className="simple-sub">Nothing has been spent yet. Watch it, or tap any scene to change it.</p>
      {showHints ? (
        <div className="simple-hints" role="note" aria-label="How this screen works">
          <span>▶ Watch your cut</span>
          <span>Tap any scene to change it</span>
          <span>Share or render when you're happy</span>
          <button aria-label="Dismiss hints" onClick={dismissHints} type="button">✕</button>
        </div>
      ) : null}
      <div className="simple-player" ref={playerRef}>
        {onTitleCard || onEndCard ? (
          <div className="simple-title-card">
            {onTitleCard ? (
              <>
                <small>A Kinetix previsualization</small>
                <strong>{project.title}</strong>
              </>
            ) : (
              <>
                <strong>{project.title}</strong>
                <small>end of cut · nothing rendered yet</small>
              </>
            )}
          </div>
        ) : displaySrc ? (
          <img src={displaySrc} alt={displayScene?.slugline || "Scene frame"} />
        ) : (
          <span className="simple-player-empty">No boards yet</span>
        )}
        {watchCaption && playing && !onTitleCard && !onEndCard ? (
          <span aria-live="polite" className="simple-player-subtitle">
            {watchCaption.speaker ? <b>{watchCaption.speaker}</b> : null}
            {watchCaption.text}
          </span>
        ) : null}
        {playlist.length ? (
          <button
            className="simple-player-watch"
            onClick={() => (playing ? setPlayIndex(null) : startWatch())}
            type="button"
            aria-label={playing ? "Pause" : "Watch the cut"}
          >
            {playing ? "❚❚" : "▶"}
          </button>
        ) : null}
        {playEntry && displayScene ? (
          <span className="simple-player-caption">
            Scene {String(displayScene.number).padStart(2, "0")} · {displayScene.slugline}
            {playEntry.frame.label ? ` — ${playEntry.frame.label}` : ""}
          </span>
        ) : null}
        {scene && !playing && !readOnly ? (
          <button className="simple-player-edit" onClick={() => setSceneOpen(true)} type="button">
            Change this scene
          </button>
        ) : null}
      </div>
      <div className="simple-thumbs" role="tablist" aria-label="Scenes">
        {project.scenes.map((item) => {
          const src = sceneCover(item);
          const hasFailed = item.frames.some((frame) => frame.generationStatus === "failed");
          return (
            <button
              className={`${item.id === scene?.id ? "active" : ""}${hasFailed ? " warn" : ""}`}
              key={item.id}
              role="tab"
              aria-selected={item.id === scene?.id}
              onClick={() => {
                setPlayIndex(null);
                setSelectedSceneId(item.id);
              }}
              type="button"
              title={hasFailed ? `${item.slugline} — some boards failed; open to retry` : item.slugline}
            >
              {src ? <img loading="lazy" src={`${src}${src.includes("?") ? "&" : "?"}w=192`} alt="" /> : <span>{String(item.number).padStart(2, "0")}</span>}
            </button>
          );
        })}
      </div>
      {people.some((person) => person.inScript) || places.length ? (
        <div className="simple-meet" aria-label="Your cast and places">
          <span className="simple-eyebrow">Your cast &amp; places — tap to browse looks</span>
          <div className="simple-meet-strip">
            {people.filter((person) => person.inScript).map((person) => {
              const src = lookSrc(person.defaultLook, 128);
              return (
                <button className="simple-meet-chip" key={person.name} onClick={() => setCastFor({ owner: person, isPlace: false })} type="button" title={`${person.name} — every look`}>
                  <span className="simple-meet-face">
                    <i>{initials(person.name)}</i>
                    {src ? <img loading="lazy" src={src} alt="" onError={(event) => { event.currentTarget.style.display = "none"; }} /> : null}
                  </span>
                  <small>{person.name}</small>
                </button>
              );
            })}
            {places.map((place) => {
              const src = lookSrc(place.defaultLook, 192);
              return src ? (
                <button className="simple-meet-chip" key={`place-${place.name}`} onClick={() => setCastFor({ owner: place, isPlace: true })} type="button" title={`${place.name} — every plate`}>
                  <span className="simple-meet-face place"><img loading="lazy" src={src} alt="" /></span>
                  <small>{place.name}</small>
                </button>
              ) : null;
            })}
          </div>
        </div>
      ) : null}
      <div className="simple-cta-bar row">
        {!readOnly ? (
          <button className="simple-pill" onClick={() => scene && setSceneOpen(true)} type="button">
            Change something
          </button>
        ) : null}
        {playlist.length ? (
          <button className="simple-pill" onClick={startPresent} type="button" title="Fullscreen playback with title card">
            Present
          </button>
        ) : null}
        {playlist.length && animaticSupported() ? (
          <button
            className="simple-pill"
            disabled={Boolean(exportProgress)}
            onClick={() => void handleSaveVideo()}
            title="Record the cut — boards, captions and voice — as a video file"
            type="button"
          >
            Save as video
          </button>
        ) : null}
        <button className="simple-pill" onClick={() => setShowPitch(true)} type="button">
          {readOnly ? "See the pitch" : "Share pitch"}
        </button>
        {!readOnly ? (
          <button
            className="simple-pill"
            disabled={isSharingLink}
            onClick={onShareLink}
            title="Copy a link anyone can open — they can watch the cut and see the pitch, not change it"
            type="button"
          >
            {isSharingLink ? "Making link…" : "Copy share link"}
          </button>
        ) : null}
        {!readOnly ? (
          <button className="simple-pill primary" onClick={() => setShowRender(true)} type="button">
            Send to render
          </button>
        ) : null}
      </div>
      {!readOnly ? <SimpleStudioCheck /> : null}
      {!readOnly ? <SimpleFinishedVideos projectId={project.id} /> : null}
      <p className="simple-footnote">← → to flip scenes · rendering uses your EvoLink budget, with the exact cost shown first</p>
      {sceneOpen && scene ? (
        <SceneSheet
          scene={scene}
          project={project}
          people={people}
          places={places}
          beat={project.agentBeats.find((item) => item.sceneId === scene.id)?.purpose}
          takes={project.sceneTakes?.[scene.id] || []}
          isBusy={isEasyModeRunning}
          onClose={() => setSceneOpen(false)}
          onRemake={(request) => {
            setSceneOpen(false);
            onRemakeScene(request);
          }}
          onRestoreTake={onRestoreTake}
          onSaveNote={onSaveSceneNote}
        />
      ) : null}
      {showPitch ? (
        <PitchView
          project={project}
          people={people}
          places={places}
          readOnly={readOnly}
          onMakePoster={onMakePoster}
          isMakingPoster={isMakingPoster}
          onClose={() => setShowPitch(false)}
        />
      ) : null}
      {castFor ? (
        <CastBrowser
          owner={castFor.owner}
          isPlace={castFor.isPlace}
          project={project}
          onClose={() => setCastFor(null)}
        />
      ) : null}
      {exportNote ? (
        <p className="simple-export-note" role="status">{exportNote}</p>
      ) : null}
      {exportProgress ? (
        <div aria-label="Filming your animatic" aria-modal="true" className="simple-export-overlay" role="dialog">
          <div className="simple-export-card">
            <strong>Filming your animatic</strong>
            <p>
              It records in real time — {formatClock(exportProgress.elapsedSeconds)} of {formatClock(exportProgress.totalSeconds)}.
              Keep this tab visible; you'll hear the voice reads as it films.
            </p>
            <div aria-hidden="true" className="simple-export-bar">
              <span style={{ width: `${Math.min(100, (exportProgress.elapsedSeconds / Math.max(1, exportProgress.totalSeconds)) * 100)}%` }} />
            </div>
            <button className="simple-pill" onClick={() => exportAbortRef.current?.abort()} type="button">
              Cancel
            </button>
          </div>
        </div>
      ) : null}
      {showRender ? (
        <RenderSheet
          project={project}
          boardCount={playlist.length}
          isSaving={isSavingPackage}
          onSend={onSendToRender}
          onClose={() => setShowRender(false)}
        />
      ) : null}
    </main>
  );
}

/**
 * Simple-mode "Send to render": rights confirmation in plain words, then the dry-run package is
 * built in place — no detour to the Pro workspace, no spend, exact-cost gate comes with live render.
 */
function RenderSheet({
  project,
  boardCount,
  isSaving,
  onSend,
  onClose
}: {
  project: FilmProject;
  boardCount: number;
  isSaving: boolean;
  onSend: () => Promise<boolean>;
  onClose: () => void;
}) {
  useEscape(onClose);
  const [rightsOk, setRightsOk] = useState(false);
  const [watchedOk, setWatchedOk] = useState(false);
  const [result, setResult] = useState<"idle" | "done" | "failed">("idle");
  const voices = project.characters.filter((character) => character.voiceStatus === "assigned").length;
  const minutes = Math.floor(project.runtimeSeconds / 60);
  const seconds = project.runtimeSeconds % 60;
  return (
    <div className="simple-sheet-backdrop" role="presentation" onClick={onClose}>
      <section className="simple-sheet" role="dialog" aria-modal="true" aria-label="Send to render" onClick={(event) => event.stopPropagation()}>
        {result === "done" ? (
          <>
            <span className="simple-render-check" aria-hidden="true">✓</span>
            <h2>Your film is packaged.</h2>
            <p className="simple-sub small">
              Everything — boards, your photos, voices, timing — is bundled for EvoLink Seedance.
              Nothing was spent. When live rendering opens, this exact package is what renders,
              and you'll approve the cost first.
            </p>
            <button className="simple-pill primary big" onClick={onClose} type="button">Done</button>
          </>
        ) : (
          <>
            <h2>Send to render</h2>
            <p className="simple-sub small">
              {project.scenes.length} scenes · {boardCount} boards · {voices} voice{voices === 1 ? "" : "s"} · {minutes}:{String(seconds).padStart(2, "0")} target
            </p>
            <div className="simple-render-checks">
              <label>
                <input checked={rightsOk} onChange={(event) => setRightsOk(event.target.checked)} type="checkbox" />
                <span>The script, faces, voices, and brands in this film are mine to use.</span>
              </label>
              <label>
                <input checked={watchedOk} onChange={(event) => setWatchedOk(event.target.checked)} type="checkbox" />
                <span>I've watched this cut and it's the version I want.</span>
              </label>
            </div>
            <button
              className="simple-pill primary big"
              disabled={!rightsOk || !watchedOk || isSaving}
              onClick={() => {
                void onSend().then((ok) => setResult(ok ? "done" : "failed"));
              }}
              type="button"
            >
              {isSaving ? "Packaging…" : "Package my film"}
            </button>
            {result === "failed" ? (
              <p className="simple-footnote">The package API wasn't reachable, so a local package was saved instead — your work is safe.</p>
            ) : (
              <p className="simple-footnote">Dry run · $0 · EvoLink is never called without your cost approval.</p>
            )}
          </>
        )}
      </section>
    </div>
  );
}

/** One-tap pitch package: a clean, print-ready page — title, cast, places, and the boards in order. */
function PitchView({
  project,
  people,
  places,
  readOnly = false,
  onMakePoster,
  isMakingPoster,
  onClose
}: {
  project: FilmProject;
  people: MatchedPerson[];
  places: MatchedPlace[];
  readOnly?: boolean;
  onMakePoster: () => void;
  isMakingPoster: boolean;
  onClose: () => void;
}) {
  useEscape(onClose);
  const castWithFaces = people.filter((person) => person.inScript);
  // The film's real logline from the analysis; the agent objective and synopsis are fallbacks.
  const logline = useMemo(() => {
    const fromAnalysis = projectLogline(project);
    if (fromAnalysis) return fromAnalysis;
    const plan = agentPlanSummary(project.apiPlan || {}, project);
    return plan.objective || project.scenes[0]?.synopsis || project.scriptExcerpt.slice(0, 180);
  }, [project]);
  const posterSrc = project.posterAsset ? assetDisplayUrl(project.posterAsset) : "";
  return (
    <div className="simple-pitch-backdrop" role="presentation">
      <div className="simple-pitch-toolbar simple-no-print">
        <button className="simple-pill" onClick={onClose} type="button">Close</button>
        {!readOnly ? (
          <button className="simple-pill" onClick={onMakePoster} disabled={isMakingPoster} type="button">
            {isMakingPoster ? "Making poster… ~30s" : posterSrc ? "Re-make poster" : "Make poster"}
          </button>
        ) : null}
        <button className="simple-pill primary" onClick={() => window.print()} type="button">Save as PDF / Print</button>
      </div>
      <article className="simple-pitch-page" aria-label="Pitch package">
        <header className="simple-pitch-cover">
          {posterSrc ? <img className="simple-pitch-poster" src={posterSrc} alt={`${project.title} poster`} /> : null}
          <span className="simple-eyebrow">A film pitch</span>
          <h1>{project.title}</h1>
          <p>{logline}</p>
          <small>{project.scenes.length} scenes · {project.genre || "drama"} · made with Kinetix Film</small>
        </header>
        <section>
          <h3>The cast</h3>
          <div className="simple-pitch-cast">
            {castWithFaces.map((person) => {
              const src = lookSrc(person.defaultLook, 320);
              return (
                <figure key={person.name}>
                  {src ? <img src={src} alt={person.name} /> : <span className="simple-person-initials">{initials(person.name)}</span>}
                  <figcaption>{person.name}</figcaption>
                </figure>
              );
            })}
          </div>
        </section>
        {places.length ? (
          <section>
            <h3>The world</h3>
            <div className="simple-pitch-places">
              {places.map((place) => {
                const src = lookSrc(place.defaultLook, 512);
                return src ? (
                  <figure key={place.name}>
                    <img src={src} alt={place.name} />
                    <figcaption>{place.name}</figcaption>
                  </figure>
                ) : null;
              })}
            </div>
          </section>
        ) : null}
        <section>
          <h3>The film</h3>
          {project.scenes.map((item) => {
            const src = sceneCover(item);
            return (
              <div className="simple-pitch-scene" key={item.id}>
                {src ? <img src={src} alt={item.slugline} /> : null}
                <div>
                  <strong>Scene {String(item.number).padStart(2, "0")} · {item.slugline}</strong>
                  <p>{item.synopsis}</p>
                  {project.sceneNotes?.[item.id]?.trim() ? (
                    <p className="simple-pitch-note">Director's note — {project.sceneNotes[item.id].trim()}</p>
                  ) : null}
                </div>
              </div>
            );
          })}
        </section>
        <footer>Storyboard previsualization — final render pending director approval.</footer>
      </article>
    </div>
  );
}

function SceneSheet({
  scene,
  project,
  people,
  places,
  beat,
  takes,
  isBusy,
  onClose,
  onRemake,
  onRestoreTake,
  onSaveNote
}: {
  scene: Scene;
  project: FilmProject;
  people: MatchedPerson[];
  places: MatchedPlace[];
  beat?: string;
  takes: SceneTake[];
  isBusy: boolean;
  onClose: () => void;
  onRemake: (request: SimpleSceneRemakeRequest) => void;
  onRestoreTake: (sceneId: string, takeId: string) => void;
  onSaveNote: (sceneId: string, text: string) => void;
}) {
  useEscape(onClose);
  const dialogue = useMemo(() => sceneDialogue(project, scene.id), [project, scene.id]);
  const directorPage = useMemo(() => sceneDirectorPage(project, scene.id), [project, scene.id]);
  const cast = useMemo(
    () => people.filter((person) => scene.characters.some((name) => name.toLowerCase() === person.name.toLowerCase())),
    [people, scene]
  );
  const scenePlace = useMemo(() => places.find((place) => place.sceneIds.includes(scene.id)), [places, scene]);
  const [overrides, setOverrides] = useState<Record<string, AssetLook>>({});
  const [placeOverride, setPlaceOverride] = useState<AssetLook | undefined>(undefined);
  const [pickerFor, setPickerFor] = useState<LookOwner | null>(null);
  const [pickerIsPlace, setPickerIsPlace] = useState(false);
  const [shots, setShots] = useState(scene.frames.filter((frame) => !frame.disabled).length || 6);
  // Single-shot re-make: tap a board below the frame to fix just that one.
  const [selectedShotId, setSelectedShotId] = useState<string | null>(null);
  useEffect(() => setSelectedShotId(null), [scene.id]);
  const failedCount = scene.frames.filter((frame) => frame.generationStatus === "failed").length;
  const cover = sceneCover(scene);
  const sceneBoards = scene.frames.filter((frame) => !frame.disabled);
  const selectedShot = sceneBoards.find((frame) => frame.id === selectedShotId) || null;
  const selectedShotIndex = selectedShot ? sceneBoards.indexOf(selectedShot) : -1;
  const note = project.sceneNotes?.[scene.id] ?? "";

  function currentLook(person: MatchedPerson): AssetLook | undefined {
    return overrides[person.name] || pickLookForScene(person, scene)?.look || undefined;
  }

  const currentPlaceLook = placeOverride
    || (scenePlace ? pickLookForScene(scenePlace, scene)?.look : undefined)
    || scenePlace?.defaultLook;

  return (
    <div className="simple-sheet-backdrop" role="presentation" onClick={onClose}>
      <section className="simple-sheet wide" role="dialog" aria-modal="true" aria-label={`Scene ${scene.number}`} onClick={(event) => event.stopPropagation()}>
        <header className="simple-sheet-head">
          <h2>Scene {scene.number} · {scene.slugline}</h2>
          <p>{scene.synopsis}</p>
          {beat ? <p className="simple-scene-why">Why this scene: {beat}</p> : null}
        </header>
        <div className="simple-scene-body">
          <div className="simple-scene-stage">
            <div className="simple-scene-frame">
              {selectedShot ? (
                <img src={frameDisplayUrl(selectedShot)} alt={selectedShot.label || scene.slugline} />
              ) : cover ? (
                <img src={cover} alt={scene.slugline} />
              ) : (
                <span className="simple-player-empty">No boards yet</span>
              )}
            </div>
            {sceneBoards.length > 1 ? (
              <>
                <div className="simple-shot-strip" role="listbox" aria-label="Boards in this scene — tap one to re-make just that shot">
                  {sceneBoards.map((frame, index) => {
                    const untouchable = frame.locked || frame.source === "uploaded";
                    const isSelected = frame.id === selectedShotId;
                    const src = frameDisplayUrl(frame, 192);
                    return (
                      <button
                        aria-selected={isSelected}
                        className={`simple-shot${isSelected ? " active" : ""}${frame.generationStatus === "failed" ? " warn" : ""}`}
                        disabled={untouchable}
                        key={frame.id}
                        onClick={() => setSelectedShotId(isSelected ? null : frame.id)}
                        role="option"
                        title={untouchable ? `Shot ${index + 1} — yours, never overwritten` : `Shot ${index + 1} — tap to re-make just this one`}
                        type="button"
                      >
                        {src ? <img loading="lazy" src={src} alt="" /> : <span>{index + 1}</span>}
                        {untouchable ? <i aria-hidden="true">🔒</i> : null}
                      </button>
                    );
                  })}
                </div>
                <p className="simple-footnote center">{selectedShot ? `Shot ${selectedShotIndex + 1} selected — only this board will be re-made.` : "Tap a board to re-make just that shot."}</p>
              </>
            ) : null}
          </div>
          <div className="simple-scene-side">
            <span className="simple-eyebrow">In this scene — tap to change</span>
            {cast.length ? cast.map((person) => {
              const look = currentLook(person);
              return (
                <button
                  className="simple-cast-row"
                  key={person.name}
                  onClick={() => {
                    if (!person.looks.length) return;
                    setPickerIsPlace(false);
                    setPickerFor(person);
                  }}
                  type="button"
                  disabled={!person.looks.length}
                >
                  <span className="simple-cast-thumb">
                    {look ? <img src={lookSrc(look, 128)} alt="" /> : <i>{initials(person.name)}</i>}
                  </span>
                  <span className="simple-cast-meta">
                    <strong>{person.name}</strong>
                    <small>{look ? `${look.parsed.lookLabel}${overrides[person.name] ? "" : " · picked for you"}` : "Look made for you"}</small>
                  </span>
                  {person.looks.length ? <b>›</b> : null}
                </button>
              );
            }) : <p className="simple-footnote">No named cast in this scene.</p>}
            {scenePlace ? (
              <button
                className="simple-cast-row"
                onClick={() => {
                  setPickerIsPlace(true);
                  setPickerFor(scenePlace);
                }}
                type="button"
              >
                <span className="simple-cast-thumb place">
                  {currentPlaceLook ? <img src={lookSrc(currentPlaceLook, 128)} alt="" /> : <i>{initials(scenePlace.name)}</i>}
                </span>
                <span className="simple-cast-meta">
                  <strong>{scenePlace.name}</strong>
                  <small>{currentPlaceLook ? `${currentPlaceLook.parsed.lookLabel}${placeOverride ? "" : " · picked for you"}` : "Plate made for you"}</small>
                </span>
                <b>›</b>
              </button>
            ) : null}
            <span className="simple-eyebrow">Adjust this scene</span>
            <div className="simple-chiprow">
              <small>Shots</small>
              {[3, 6, 9].map((count) => (
                <button className={shots === count ? "active" : ""} key={count} onClick={() => setShots(count)} type="button">{count}</button>
              ))}
            </div>
            {failedCount ? (
              <p className="simple-failed-hint" role="status">
                {failedCount} board{failedCount === 1 ? "" : "s"} didn't generate — Re-make this scene retries {failedCount === 1 ? "it" : "them"}.
              </p>
            ) : null}
            <button
              className="simple-pill primary"
              disabled={isBusy}
              onClick={() =>
                onRemake(
                  selectedShot
                    ? { sceneId: scene.id, frameIds: [selectedShot.id], lookOverrides: overrides, placeLook: placeOverride }
                    : { sceneId: scene.id, panelsPerScene: shots, lookOverrides: overrides, placeLook: placeOverride }
                )
              }
              type="button"
            >
              {isBusy ? "Working…" : selectedShot ? `Re-make shot ${selectedShotIndex + 1} only` : failedCount ? "Re-make this scene (retry)" : "Re-make this scene"}
            </button>
            <p className="simple-footnote">
              {selectedShot
                ? "≈ 30 seconds · the rest of the scene doesn't change · your current boards are kept as a take"
                : "≈ 30 seconds per shot · the other scenes don't change · your current boards are kept as a take"}
            </p>
            {takes.length ? (
              <>
                <span className="simple-eyebrow">Takes</span>
                <div className="simple-takes" aria-label="Previous takes of this scene">
                  {takes.map((take) => {
                    const thumb = take.frames.find((frame) => frame.imageUrl || frame.sourceUrl || frame.storageKey);
                    return (
                      <button className="simple-take" key={take.id} onClick={() => onRestoreTake(scene.id, take.id)} type="button" title={`Restore ${take.label}`}>
                        <span className="simple-take-thumb">
                          {thumb ? <img src={frameDisplayUrl(thumb, 192)} alt="" /> : null}
                        </span>
                        <small>{take.label} · use</small>
                      </button>
                    );
                  })}
                </div>
              </>
            ) : null}
          </div>
        </div>
        <div className="simple-scene-notes">
          <span className="simple-eyebrow">Your notes</span>
          <textarea
            defaultValue={note}
            key={scene.id}
            onBlur={(event) => {
              if (event.target.value !== note) onSaveNote(scene.id, event.target.value);
            }}
            placeholder="Anything you want to remember about this scene — performance, pacing, what to fix next pass…"
            rows={2}
          />
          <p className="simple-footnote">Saved with your film · printed on the pitch page</p>
        </div>
        {dialogue.length || directorPage ? (
          <div className="simple-scene-page">
            {dialogue.length ? (
              <div className="simple-words">
                <span className="simple-eyebrow">The words</span>
                <div className="simple-words-lines">
                  {dialogue.map((line, index) => (
                    <p key={index}>
                      {line.speaker ? <b>{line.speaker}</b> : null}
                      {line.text}
                    </p>
                  ))}
                </div>
              </div>
            ) : null}
            {directorPage ? (
              <div className="simple-director-notes">
                <span className="simple-eyebrow">Director's page</span>
                {directorPage.emotionalBeat ? <p><b>Beat</b>{directorPage.emotionalBeat}</p> : null}
                {directorPage.directorIntent ? <p><b>Intent</b>{directorPage.directorIntent}</p> : null}
                {directorPage.visualLanguage ? <p><b>Visual</b>{directorPage.visualLanguage}</p> : null}
                {directorPage.soundDesign ? <p><b>Sound</b>{directorPage.soundDesign}</p> : null}
              </div>
            ) : null}
          </div>
        ) : null}
        {pickerFor ? (
          <LookPicker
            owner={pickerFor}
            selected={pickerIsPlace ? currentPlaceLook : currentLook(pickerFor as MatchedPerson)}
            sceneNumber={scene.number}
            isPlace={pickerIsPlace}
            onPick={(look) => {
              if (pickerIsPlace) setPlaceOverride(look);
              else setOverrides((current) => ({ ...current, [pickerFor.name]: look }));
              setPickerFor(null);
            }}
            onClose={() => setPickerFor(null)}
          />
        ) : null}
      </section>
    </div>
  );
}

/** Browse one person's or place's full look library and see which scenes use them. */
function CastBrowser({
  owner,
  isPlace,
  project,
  onClose
}: {
  owner: LookOwner;
  isPlace: boolean;
  project: FilmProject;
  onClose: () => void;
}) {
  useEscape(onClose);
  const appearsIn = isPlace
    ? project.scenes.filter((scene) => (owner as MatchedPlace).sceneIds?.includes(scene.id))
    : project.scenes.filter((scene) => scene.characters.some((name) => name.toLowerCase() === owner.name.toLowerCase()));
  return (
    <div className="simple-sheet-backdrop" role="presentation" onClick={onClose}>
      <section className="simple-sheet" role="dialog" aria-modal="true" aria-label={`${owner.name} — looks`} onClick={(event) => event.stopPropagation()}>
        <h2>{owner.name}</h2>
        <p className="simple-sub small">
          {appearsIn.length
            ? `In scene${appearsIn.length === 1 ? "" : "s"} ${appearsIn.map((scene) => scene.number).join(", ")}`
            : "Not in any scene yet"}
          {owner.looks.length ? ` · ${owner.looks.length} look${owner.looks.length === 1 ? "" : "s"}` : ""}
        </p>
        {owner.looks.length ? (
          <div className="simple-look-grid">
            {owner.looks.map((look) => (
              <div className="simple-look-still" key={`${look.asset.assetId || look.asset.fileName}`}>
                <span className="simple-look-img">
                  <img loading="lazy" src={lookSrc(look, 384)} alt={look.parsed.lookLabel} />
                </span>
                <strong>{look.parsed.lookLabel}</strong>
                <small>{look.asset.sourceMode === "user_upload" ? "Your photo" : "Made for you"}</small>
              </div>
            ))}
          </div>
        ) : (
          <p className="simple-footnote">No looks yet — they'll appear when the film is made.</p>
        )}
        <p className="simple-footnote">To change {isPlace ? "this place" : "their look"} in a scene, open that scene and tap {isPlace ? "the place" : "their face"}.</p>
        <button className="simple-pill primary" onClick={onClose} type="button">Done</button>
      </section>
    </div>
  );
}

function LookPicker({
  owner,
  selected,
  sceneNumber,
  isPlace,
  onPick,
  onClose
}: {
  owner: LookOwner;
  selected?: AssetLook;
  sceneNumber: number;
  isPlace?: boolean;
  onPick: (look: AssetLook) => void;
  onClose: () => void;
}) {
  useEscape(onClose);
  return (
    <div className="simple-sheet-backdrop inner" role="presentation" onClick={onClose}>
      <section className="simple-sheet" role="dialog" aria-modal="true" aria-label={`${owner.name} looks`} onClick={(event) => event.stopPropagation()}>
        <h2>{owner.name} in this scene</h2>
        <p className="simple-sub small">From your uploads · tap any look to use it in scene {sceneNumber} only</p>
        <div className="simple-look-grid">
          {owner.looks.map((look) => {
            const isSelected = selected?.asset.assetId === look.asset.assetId && selected?.asset.fileName === look.asset.fileName;
            return (
              <button className={isSelected ? "active" : ""} key={`${look.asset.assetId || look.asset.fileName}`} onClick={() => onPick(look)} type="button">
                <span className="simple-look-img">
                  <img loading="lazy" src={lookSrc(look, 384)} alt={look.parsed.lookLabel} />
                  {isSelected ? <b>✓ In this scene</b> : null}
                </span>
                <strong>{look.parsed.lookLabel}</strong>
                <small>{isSelected ? `Look ${look.parsed.lookNumber ?? "—"}` : "Tap to use here"}</small>
              </button>
            );
          })}
        </div>
        <p className="simple-footnote">{isPlace ? "This place" : "Their look"} everywhere else stays the same.</p>
      </section>
    </div>
  );
}

/** Pre-analysis uploads: two wells, zero questions. Audio anywhere becomes a voice read. */
function SimpleAssetsSheet({
  project,
  onUpload,
  onClose
}: {
  project: FilmProject;
  onUpload: (bucket: "characterCards" | "sceneReferences" | "voiceAudio", files: File[]) => void;
  onClose: () => void;
}) {
  useEscape(onClose);
  const peopleInputRef = useRef<HTMLInputElement>(null);
  const placesInputRef = useRef<HTMLInputElement>(null);
  const manifest = project.uploadedAssets;
  const counts = {
    people: (manifest?.characterCards || []).filter((a) => a.sourceMode === "user_upload" && !a.disabled).length,
    places: (manifest?.sceneReferences || []).filter((a) => a.sourceMode === "user_upload" && !a.disabled).length,
    voices: (manifest?.voiceAudio || []).filter((a) => a.sourceMode === "user_upload" && !a.disabled).length
  };

  function routeFiles(bucket: "characterCards" | "sceneReferences", list: FileList | File[] | null) {
    const files = Array.from(list || []);
    if (!files.length) return;
    const audio = files.filter((file) => file.type.startsWith("audio/") || /\.(mp3|m4a|wav|aac|ogg|flac)$/i.test(file.name));
    const visuals = files.filter((file) => !audio.includes(file));
    if (visuals.length) onUpload(bucket, visuals);
    if (audio.length) onUpload("voiceAudio", audio);
  }

  return (
    <div className="simple-sheet-backdrop" role="presentation" onClick={onClose}>
      <section className="simple-sheet" role="dialog" aria-modal="true" aria-label="Add your own cast and places" onClick={(event) => event.stopPropagation()}>
        <h2>Your film, your faces</h2>
        <p className="simple-sub small">Anything you add is used first. ChatGPT only invents what's missing.</p>
        <input
          ref={peopleInputRef}
          className="visually-hidden"
          type="file"
          multiple
          accept="image/*,audio/*"
          onChange={(event) => {
            routeFiles("characterCards", event.target.files);
            event.target.value = "";
          }}
        />
        <input
          ref={placesInputRef}
          className="visually-hidden"
          type="file"
          multiple
          accept="image/*,audio/*"
          onChange={(event) => {
            routeFiles("sceneReferences", event.target.files);
            event.target.value = "";
          }}
        />
        <div className="simple-asset-wells">
          <button
            className="simple-asset-well"
            onClick={() => peopleInputRef.current?.click()}
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => {
              event.preventDefault();
              routeFiles("characterCards", event.dataTransfer.files);
            }}
            type="button"
          >
            <span aria-hidden="true">🧑‍🎤</span>
            <strong>People</strong>
            <small>{counts.people ? `${counts.people} added` : "Photos of your cast"}</small>
          </button>
          <button
            className="simple-asset-well"
            onClick={() => placesInputRef.current?.click()}
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => {
              event.preventDefault();
              routeFiles("sceneReferences", event.dataTransfer.files);
            }}
            type="button"
          >
            <span aria-hidden="true">🏙</span>
            <strong>Places</strong>
            <small>{counts.places ? `${counts.places} added` : "Streets, rooms, storefronts"}</small>
          </button>
        </div>
        {counts.voices ? <p className="simple-footnote center">🎙 {counts.voices} voice read{counts.voices === 1 ? "" : "s"} added</p> : null}
        <p className="simple-footnote">
          Tip: name files like <i>Elle Look 01 - Master.png</i> and each scene picks the right look on its own.
          Audio files can go in either spot — they become voice reads.
        </p>
        <button className="simple-pill primary" onClick={onClose} type="button">Done</button>
      </section>
    </div>
  );
}

function formatClock(seconds: number): string {
  const whole = Math.max(0, Math.round(seconds));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
}

/* ---------- Orchestrator ---------- */

export function SimpleMode({
  project,
  isAnalyzing,
  isEasyModeRunning,
  easyModeProgress,
  easyModeOptions,
  onDropScript,
  onAnalyze,
  onJustMakeIt,
  onTrySample,
  onNewFromImages,
  isJustMaking,
  apiStatus,
  engineStale,
  onMakeFilm,
  onRemakeScene,
  onRestoreTake,
  onSaveSceneNote,
  onShareLink,
  isSharingLink,
  onMakePoster,
  isMakingPoster,
  onUploadPersonPhoto,
  onUploadPlacePhotos,
  onUploadAssets,
  onOpenPro,
  onSendToRender,
  isSavingPackage,
  notice,
  films,
  isLoadingFilms,
  onRefreshFilms,
  onOpenFilm,
  onNewFilm
}: SimpleModeProps) {
  const derived = deriveScreen(project, isAnalyzing, isEasyModeRunning, easyModeProgress);
  // While the one-tap chain is between analysis and assembly, keep showing the reading state.
  const screen = isJustMaking && (derived === "home" || derived === "film") ? "reading" : derived;
  const [showFilms, setShowFilms] = useState(false);
  const [showAssets, setShowAssets] = useState(false);
  const people = useMemo(() => matchPeople(project.characters, project.uploadedAssets), [project.characters, project.uploadedAssets]);
  const places = useMemo(() => matchPlaces(project.scenes, project.uploadedAssets), [project.scenes, project.uploadedAssets]);
  return (
    <div className="simple-mode">
      <nav className="simple-nav">
        <span className="simple-brand">
          <b>K</b>
          Kinetix Film
        </span>
        <span className="simple-nav-actions">
          <button
            className="simple-nav-new"
            onClick={onNewFilm}
            disabled={isAnalyzing || isEasyModeRunning}
            type="button"
            title="Start a new film from a fresh script"
          >
            + New film
          </button>
          <button
            className="simple-link"
            onClick={() => {
              onRefreshFilms();
              setShowFilms(true);
            }}
            type="button"
          >
            Your films
          </button>
          <button className="simple-link muted" onClick={onOpenPro} type="button">
            Pro
          </button>
        </span>
      </nav>
      {apiStatus === "offline" ? (
        <div className="simple-offline" role="alert">
          <strong>Can't reach the Kinetix engine.</strong>
          <span>Start the API server (Terminal 1), then retry.</span>
          <button className="simple-pill" onClick={() => window.location.reload()} type="button">Retry</button>
        </div>
      ) : engineStale ? (
        <div className="simple-offline stale" role="alert">
          <strong>The engine is running older code.</strong>
          <span>Restart the API server to pick up the newest features.</span>
          <button className="simple-pill" onClick={() => window.location.reload()} type="button">I restarted it</button>
        </div>
      ) : null}
      {screen === "home" ? <SimpleHome project={project} onDropScript={onDropScript} onAnalyze={onAnalyze} onJustMakeIt={onJustMakeIt} onTrySample={onTrySample} onNewFromImages={onNewFromImages} onAddAssets={() => setShowAssets(true)} /> : null}
      {screen === "reading" ? <SimpleReading project={project} /> : null}
      {screen === "film" ? (
        <SimpleFilm
          project={project}
          people={people}
          places={places}
          onMakeFilm={onMakeFilm}
          onJustMakeIt={onJustMakeIt}
          onUploadPersonPhoto={onUploadPersonPhoto}
          onUploadPlacePhotos={onUploadPlacePhotos}
          easyModeOptions={easyModeOptions}
        />
      ) : null}
      {screen === "assembling" ? <SimpleAssembling project={project} progress={easyModeProgress} /> : null}
      {screen === "cut" ? (
        <SimpleCut
          project={project}
          people={people}
          places={places}
          onRemakeScene={onRemakeScene}
          onRestoreTake={onRestoreTake}
          onSaveSceneNote={onSaveSceneNote}
          onShareLink={onShareLink}
          isSharingLink={isSharingLink}
          onMakePoster={onMakePoster}
          isMakingPoster={isMakingPoster}
          onSendToRender={onSendToRender}
          isSavingPackage={isSavingPackage}
          isEasyModeRunning={isEasyModeRunning}
        />
      ) : null}
      {showAssets ? (
        <SimpleAssetsSheet project={project} onUpload={onUploadAssets} onClose={() => setShowAssets(false)} />
      ) : null}
      {showFilms ? (
        <FilmsSheet
          currentProject={project}
          films={films}
          isLoading={isLoadingFilms}
          onOpen={onOpenFilm}
          onNew={onNewFilm}
          onClose={() => setShowFilms(false)}
        />
      ) : null}
      <SimpleToast notice={notice} />
    </div>
  );
}

/* ---------- Studio check: the doctor, in plain language, only when something's off ---------- */

function SimpleStudioCheck() {
  const [report, setReport] = useState<DoctorReport | null>(null);
  const [engineDown, setEngineDown] = useState(false);
  const [moreHelp, setMoreHelp] = useState("");
  const [asking, setAsking] = useState(false);
  useEffect(() => {
    fetchDoctor().then((r) => { setReport(r); setEngineDown(false); }).catch(() => setEngineDown(true));
  }, []);
  const issues = report ? report.checks.filter((check) => !check.ok && check.id !== "render_key") : [];
  if (!engineDown && (!report || !issues.length)) return null;
  return (
    <div className="simple-studio-check" role="status">
      <span className="simple-eyebrow">Studio check</span>
      {engineDown ? (
        <p>The film engine isn't running, so making and rendering are paused. Open Terminal in your project folder and run <code>npm run api:dev</code>, then refresh this page.</p>
      ) : (
        <>
          {issues.slice(0, 2).map((issue) => (
            <p key={issue.id}>
              <b>{issue.label}.</b> {issue.fix || issue.detail}
            </p>
          ))}
          <button
            className="simple-pill"
            disabled={asking}
            onClick={() => {
              setAsking(true);
              askAssistant("explain", { friendly: true, audience: "non-technical filmmaker", checks: issues })
                .then((result) => {
                  const answer = result.answer as { explanation?: string; nextSteps?: string[] } | undefined;
                  setMoreHelp(result.ok && answer?.explanation ? [answer.explanation, ...(answer.nextSteps || []).map((s) => `→ ${s}`)].join("\n") : result.message || "Help is unavailable right now.");
                })
                .catch(() => setMoreHelp("Help is unavailable right now."))
                .finally(() => setAsking(false));
            }}
            type="button"
          >
            {asking ? "Thinking…" : "Explain this to me"}
          </button>
          {moreHelp ? <p className="simple-studio-help">{moreHelp}</p> : null}
        </>
      )}
    </div>
  );
}

/* ---------- Your finished videos: the "where is my file?" answer ---------- */

function SimpleFinishedVideos({ projectId }: { projectId: string }) {
  const [items, setItems] = useState<ProjectExportItem[]>([]);
  useEffect(() => {
    fetchProjectExports(projectId).then((r) => setItems(r.items)).catch(() => setItems([]));
  }, [projectId]);
  if (!items.length) return null;
  const url = (key: string) => `/api/assets/object/${key.split("/").map(encodeURIComponent).join("/")}`;
  const masters = items.filter((item) => item.isMaster);
  const visible = [...masters, ...items.filter((item) => !item.isMaster)].slice(0, 5);
  return (
    <div className="simple-finished" aria-label="Your finished videos">
      <span className="simple-eyebrow">Your finished videos</span>
      <div className="simple-finished-list">
        {visible.map((item) => (
          <div className="simple-finished-row" key={item.storageKey}>
            <span className="simple-finished-name" title={item.fileName}>
              {item.isMaster ? "★ " : ""}{item.fileName}
            </span>
            <span className="simple-finished-actions">
              <small>{(item.bytes / 1e6).toFixed(1)} MB</small>
              <a href={url(item.storageKey)} rel="noreferrer" target="_blank">Watch</a>
              <a download={item.fileName} href={url(item.storageKey)}>Save</a>
            </span>
          </div>
        ))}
      </div>
      <p className="simple-finished-note">★ is a finished cut with sound. Everything here is already saved in your film's library.</p>
    </div>
  );
}
